import type { QuerySubState } from '@reduxjs/toolkit/query';

type PaginatedRequestArgs = { omit: number; select: number } & (
	| { userId: string }
	| { nickname: string }
	| { parentMediaId: string }
);

type PaginatedApiResponse<K extends string, T> = {
	receivedCount: number;
	totalLength: number;
} & {
	[P in K]: T[];
};

type DataType = { id: number } | { uid: string };

export const getPaginationParams = <K extends string, T extends DataType>(
	dataKey: K,
	prepare?: (data: T[]) => T[],
	overrideExistingId = true
) => ({
	transformResponse: (
		response: PaginatedApiResponse<K, T> | null
	): PaginatedApiResponse<K, T> => {
		if (response === null) {
			return {
				receivedCount: 0,
				totalLength: 0,
				[dataKey]: [],
			} as PaginatedApiResponse<K, T>;
		}

		const data = prepare ? prepare?.(response[dataKey]) : response[dataKey];

		return {
			[dataKey]: data,
			receivedCount: data.length,
			totalLength: data.length,
		} as PaginatedApiResponse<K, T>;
	},

	serializeQueryArgs: ({
		endpointName,
		queryArgs,
	}: {
		endpointName: string;
		queryArgs: PaginatedRequestArgs;
	}) => {
		if (typeof queryArgs !== 'object') {
			return '';
		}

		return [
			endpointName,
			'userId' in queryArgs ? queryArgs.userId : null,
			'nickname' in queryArgs ? queryArgs.nickname : null,
			'parentMediaId' in queryArgs ? queryArgs.parentMediaId : null,
		]
			.filter(Boolean)
			.join('-');
	},

	forceRefetch: ({
		currentArg,
		endpointState,
	}: {
		currentArg: PaginatedRequestArgs | undefined;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		endpointState?: QuerySubState<any>;
	}) => {
		return (
			// @ts-ignore
			(currentArg?.omit ?? 0) >= (endpointState?.data?.totalLength ?? 0)
		);
	},

	merge: (
		currentState: PaginatedApiResponse<K, T>,
		incomingState: PaginatedApiResponse<K, T>
	) => {
		currentState.receivedCount = incomingState.receivedCount;
		currentState.totalLength =
			currentState.totalLength + incomingState.receivedCount;

		incomingState[dataKey].forEach((item) => {
			const existedIndex = currentState[dataKey].findIndex((existing) => {
				const oldId = 'id' in existing ? existing.id : existing.uid;
				const newId = 'id' in item ? item.id : item.uid;
				return oldId === newId;
			});

			if (existedIndex > -1) {
				if (overrideExistingId) {
					currentState[dataKey][existedIndex] = item;
				}

				currentState.totalLength -= 1;
			} else {
				currentState[dataKey].push(item);
			}
		});
	},
});
